import type { LucideIcon } from "lucide-react";
import { BookOpen, Bot, Compass, UserCircle, Video } from "lucide-react";

export const PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=com.skutopia.academy.skutopia_academy";

export const fragmentedPlatforms = [
  "Courses live in one place. Past papers in another.",
  "Scholarships arrive as WhatsApp forwards and dead links.",
  "Summer programmes hide in email threads.",
  "When you're stuck at midnight, generic chatbots don't know your syllabus.",
  "High school students deserve one app for learning and opportunity, not a maze.",
];

/** How the app works today: Flutter main tabs */
export const ecosystemPillars: {
  title: string;
  icon: LucideIcon;
  description: string;
  why: string;
}[] = [
  {
    title: "Videos",
    icon: Video,
    description: "Short lessons and peer stories for everyday learning.",
    why: "Because attention is scarce and revision happens in pockets of time.",
  },
  {
    title: "Courses",
    icon: BookOpen,
    description: "Structured courses, quizzes, and flashcards aligned to curricula.",
    why: "Because exam prep should be organised, not scavenged across the internet.",
  },
  {
    title: "Muzanga",
    icon: Bot,
    description: "An AI tutor that explains concepts in your curriculum context.",
    why: "Because every student deserves help when a teacher is not available.",
  },
  {
    title: "Discover",
    icon: Compass,
    description: "Scholarships, summer schools, and programmes from partner organisations.",
    why: "Because opportunity should sit next to learning, not in a separate browser tab.",
  },
  {
    title: "Profile",
    icon: UserCircle,
    description: "Progress, application tools, and your account in one place.",
    why: "Because applying and studying should not require five different logins.",
  },
];

export const learnerJourney = [
  "Junior secondary",
  "Senior secondary",
  "National examinations",
  "Scholarships & summer programmes",
  "University applications",
          'Post High School & early next steps',
];

export const values = [
  {
    title: "Accessibility",
    body: "Quality learning and opportunity should reach every high school student, regardless of geography or income.",
  },
  {
    title: "Clarity",
    body: "We ship what students can use today: courses, practice, AI help, and Discover. We say clearly what is still growing.",
  },
  {
    title: "Opportunity",
    body: "Learning means more when pathways are visible. Partners publish; students discover.",
  },
  {
    title: "Community",
    body: "Study groups, chat, and peer stories make exam season easier together.",
  },
  {
    title: "Curiosity",
    body: "Questions matter. Muzanga is built to guide understanding, not just hand out answers.",
  },
  {
    title: "Integrity",
    body: "We do not claim degrees we do not grant, or partnerships we have not earned.",
  },
];

export const roadmap = [
  {
    phase: "Today",
    period: "Shipped",
    title: "Learning + Discover",
    description:
      "Videos, courses, quizzes, flashcards, Muzanga chat, student stories, and partner-published opportunities on Discover.",
  },
  {
    phase: "Near term",
    period: "Next",
    title: "Deeper matching & applications",
    description:
      "Richer scholarship matching, clearer application flows, and more partners publishing live listings.",
  },
  {
    phase: "Growing",
    period: "Building",
    title: "Stronger school & partner networks",
    description:
      "Closer ties with schools and opportunity publishers so more students see relevant programmes in time.",
  },
];

export const impactStories = [
  {
    name: "Chungu",
    role: "Grade 12 student, Zambia",
    quote:
      "Before Skutopia, I studied from random PDFs and YouTube videos. Now practice questions, Muzanga, and my study group live in one place. I walk into exams knowing I prepared properly.",
    outcome: "Confidence",
    image: "/assets/testimonials/Chungu.jpg",
  },
  {
    name: "Florence",
    role: "University student, Accra, Ghana",
    quote:
      "I found a scholarship through Discover that I would never have seen on my own. Skutopia helped me study and see a future I didn't know was possible.",
    outcome: "Opportunity",
    image: "/assets/testimonials/florence.jpg",
  },
  {
    name: "Ackim",
    role: "Grade 12 student, Lusaka",
    quote:
      "Exam season used to mean jumping between apps. Having courses and opportunities together made everything feel manageable.",
    outcome: "Focus",
    image: "/assets/testimonials/Ackim.jpg",
  },
];

export const philosophyContrast = [
  {
    label: "Scattered tools",
    points: [
      "Study content in one place, scholarships in another",
      "Generic AI with no syllabus context",
      "Dead links and WhatsApp forwards for opportunities",
      "Students left to stitch the journey alone",
    ],
  },
  {
    label: "Skutopia",
    points: [
      "Courses, practice, and Discover in one app",
      "Muzanga grounded in curriculum context",
      "Partners publish live opportunities for high school students",
      "Built for secondary learners preparing for what comes next",
    ],
  },
];
